/**
 * Block 12 PR-B Phase B3 Part C Step 23 — SectionTotal (И).
 *
 * Live total display + Save CTA + «Сформировать КП» CTA. Total computed
 * via `computeTotal(state, methodology)` (Step 14) — caller передаёт результат.
 *
 * Validation gating: Save CTA disabled когда `isValid=false` (refined schema
 * не прошла). КП CTA — отдельный flow, не блокируется validation для preview-кейсов.
 *
 * Hotfix-2A Phase A5 Step 9: YL-background total card (per прототип
 * 2.12.2:578-582) заменяет cardS pattern для visual emphasis (sum stands out
 * vs material rows). NB: «Итого» heading 22px fontWeight 600 per прототип
 * (NOT 26px SectionHeader treatment — Total is special) + amount 40px
 * fontWeight 800 font-display (Manrope Variable per ADR-0023; previous Oswald
 * superseded — TZ литерал «Bebas Neue» не используется из-за Cyrillic) + caption «±10%
 * погрешность» 16px text-separator (#5a4a36).
 *
 * Hotfix-2A Phase A5 Step 10: Save CTAs per прототип 2.12.2:586-593:
 * - «Сохранить и продолжить» — outline 56px height, fontSize 22, fontWeight 500,
 *   border 1.5px solid accent, borderRadius 14, bg transparent. Maps to `onSave`
 *   (validation-gated, disabled when isValid=false).
 * - «Сохранить и сделать коммерческое предложение» — primary YL 120px height,
 *   fontSize 26, fontWeight 500, font-display, borderRadius 14, bg-cta,
 *   border-b-2 border-b-cta-shadow (#d4a800), letterSpacing 0.02em. Maps to
 *   `onCreateQuote` (NOT validation-gated per existing JSDoc — preview flow).
 *
 * PR-C1 (ADR-0035) Phase 1 — forward-chain «Вперёд» под КП-CTA. Видна ТОЛЬКО
 * когда `forwardVisible=true` (контейнер `AnketaFormContainer` async-проверил,
 * что для projectId уже существует КП). Клик → `onForward` (контейнер делает
 * navigate на existing КП). Презентер только рендерит — IDB-lookup в контейнере
 * (ADR-0005).
 *
 * Reference: ADR-0020 §Q4, §Q11; ADR-0035; методика §1.5.
 */

import { ArrowRight } from 'lucide-react';

export interface SectionTotalProps {
  total: number;
  isValid: boolean;
  validationErrorCount?: number;
  onSave: () => void | Promise<void>;
  onCreateQuote?: () => void;
  isSaving?: boolean;
  /** PR-C1 (ADR-0035) — открыть существующий КП по цепочке. */
  onForward?: () => void;
  /** PR-C1 (ADR-0035) — КП для projectId существует → показать «Вперёд». */
  forwardVisible?: boolean;
}

export function SectionTotal({
  total,
  isValid,
  validationErrorCount,
  onSave,
  onCreateQuote,
  isSaving,
  onForward,
  forwardVisible,
}: SectionTotalProps) {
  const saveDisabled = !isValid || isSaving;
  return (
    <>
      <div
        data-testid="section-total"
        className="bg-cta rounded-2xl p-4 mx-3"
        aria-labelledby="section-total-heading"
      >
        <h2
          id="section-total-heading"
          className="text-ink m-0"
          style={{ fontSize: '22px', fontWeight: 600 }}
        >
          Итого
        </h2>
        <p
          className="text-ink font-display m-0 mt-1"
          style={{ fontSize: '40px', fontWeight: 800, lineHeight: 1.1 }}
          aria-live="polite"
          data-testid="section-total-amount"
        >
          {total.toLocaleString('ru-RU')} ₸
        </p>
        <p
          className="m-0 mt-1 text-separator"
          style={{ fontSize: '16px' }}
          data-testid="section-total-caption"
        >
          ±10% погрешность
        </p>
      </div>

      {!isValid ? (
        <p role="alert" className="text-error text-sm text-center mx-3">
          {validationErrorCount && validationErrorCount > 0
            ? `Заполните обязательные поля (ошибок: ${validationErrorCount})`
            : 'Заполните параметры изделия'}
        </p>
      ) : null}

      <div className="mx-3 flex flex-col gap-2.5">
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={saveDisabled}
          data-testid="section-total-save"
          aria-label="Сохранить и продолжить"
          aria-disabled={saveDisabled}
          className={
            'w-full rounded-[14px] border-[1.5px] border-accent bg-transparent text-ink focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80 ' +
            (saveDisabled ? 'opacity-50 cursor-not-allowed' : '')
          }
          style={{ minHeight: '56px', fontSize: '22px', fontWeight: 500 }}
        >
          {isSaving ? 'Сохраняем...' : 'Сохранить и продолжить'}
        </button>

        {onCreateQuote ? (
          <button
            type="button"
            onClick={onCreateQuote}
            data-testid="section-total-quote"
            aria-label="Сохранить и сформировать коммерческое предложение"
            className="w-full rounded-[14px] bg-cta border-b-2 border-b-cta-shadow text-ink font-display focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
            style={{
              minHeight: '120px',
              fontSize: '26px',
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}
          >
            Сохранить и сделать коммерческое предложение
          </button>
        ) : null}

        {forwardVisible && onForward ? (
          <button
            type="button"
            onClick={onForward}
            data-testid="section-total-forward"
            aria-label="Вперёд — открыть коммерческое предложение"
            className="w-full rounded-[14px] border-[1.5px] border-accent bg-card text-ink flex items-center justify-center gap-2 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
            style={{ minHeight: '56px', fontSize: '22px', fontWeight: 500 }}
          >
            Вперёд
            <ArrowRight size={22} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </>
  );
}
